export type PackedMarkerPosition = {
  left: number;
  top: number;
  size: number;
};

export type PackedMarkerKind = 'pawn' | 'cube';

const SPECIAL_PACKS: Record<number, PackedMarkerPosition[]> = {
  1: [{ left: 14, top: 14, size: 72 }],
  2: [
    { left: 4, top: 4, size: 48 },
    { left: 48, top: 48, size: 48 },
  ],
  3: [
    { left: 6, top: 8, size: 40 },
    { left: 54, top: 8, size: 40 },
    { left: 30, top: 52, size: 40 },
  ],
  4: [
    { left: 8, top: 8, size: 39 },
    { left: 53, top: 8, size: 39 },
    { left: 8, top: 53, size: 39 },
    { left: 53, top: 53, size: 39 },
  ],
  5: [
    { left: 18, top: 11, size: 29 },
    { left: 53, top: 11, size: 29 },
    { left: 1, top: 60, size: 29 },
    { left: 36, top: 60, size: 29 },
    { left: 71, top: 60, size: 29 },
  ],
  6: [
    { left: 1, top: 17, size: 28 },
    { left: 36, top: 17, size: 28 },
    { left: 71, top: 17, size: 28 },
    { left: 1, top: 55, size: 28 },
    { left: 36, top: 55, size: 28 },
    { left: 71, top: 55, size: 28 },
  ],
  7: [
    { left: 22, top: 3, size: 24 },
    { left: 54, top: 3, size: 24 },
    { left: 6, top: 38, size: 24 },
    { left: 38, top: 38, size: 24 },
    { left: 70, top: 38, size: 24 },
    { left: 22, top: 73, size: 24 },
    { left: 54, top: 73, size: 24 },
  ],
  8: [
    { left: 6, top: 3, size: 24 },
    { left: 38, top: 3, size: 24 },
    { left: 70, top: 3, size: 24 },
    { left: 22, top: 38, size: 24 },
    { left: 54, top: 38, size: 24 },
    { left: 6, top: 73, size: 24 },
    { left: 38, top: 73, size: 24 },
    { left: 70, top: 73, size: 24 },
  ],
  9: Array.from({ length: 9 }, (_, index) => ({
    left: 6 + (index % 3) * 32,
    top: 3 + Math.floor(index / 3) * 35,
    size: 24,
  })),
};

/**
 * Places equally sized piece footprints inside a square marker space. Small
 * counts use roomy, recognizable packings; large counts fall back to compact,
 * centered rows so even the full physical-piece inventory remains legible.
 */
export function packMarkers(
  count: number,
  kinds: readonly PackedMarkerKind[] = [],
): PackedMarkerPosition[] {
  if (count <= 0) return [];

  if (count === 3 && kinds.length === count && kinds.filter((kind) => kind === 'pawn').length === 1) {
    const pawnPosition = { left: 4, top: 29, size: 43 };
    const cubePositions = [
      { left: 56, top: 7, size: 37 },
      { left: 56, top: 56, size: 37 },
    ];
    let cubeIndex = 0;
    return kinds.map((kind) => (
      kind === 'pawn' ? { ...pawnPosition } : { ...cubePositions[cubeIndex++]! }
    ));
  }

  const specialPack = SPECIAL_PACKS[count];
  if (specialPack) return specialPack.map((position) => ({ ...position }));

  const columns = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / columns);
  const gap = 2.5;
  const padding = 4;
  const usable = 100 - padding * 2;
  const size = Math.min(
    (usable - gap * (columns - 1)) / columns,
    (usable - gap * (rows - 1)) / rows,
  );
  const rowCounts = Array.from({ length: rows }, () => Math.floor(count / rows));

  for (let remainder = count % rows, offset = 0; remainder > 0; remainder -= 1) {
    const center = (rows - 1) / 2;
    const direction = offset % 2 === 0 ? 1 : -1;
    const distance = Math.ceil(offset / 2);
    const row = Math.max(
      0,
      Math.min(rows - 1, Math.round(center + direction * distance)),
    );
    rowCounts[row] = (rowCounts[row] ?? 0) + 1;
    offset += 1;
  }

  const contentHeight = rows * size + (rows - 1) * gap;
  const top = (100 - contentHeight) / 2;

  return rowCounts.flatMap((rowCount, row) => {
    const rowWidth = rowCount * size + (rowCount - 1) * gap;
    const left = (100 - rowWidth) / 2;
    return Array.from({ length: rowCount }, (_, column) => ({
      left: left + column * (size + gap),
      top: top + row * (size + gap),
      size,
    }));
  });
}
